// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

export type Nullish = null | undefined;

export type Defined<T> = Exclude<T, Nullish>;

export type FalsyPrimitive = false | 0 | -0 | bigint | '' | Nullish;

export type FalsyValue = FalsyPrimitive | typeof NaN;

export type TruthyPitfall =
  | readonly unknown[]
  | Record<string, unknown>
  | '0'
  | 'false';

export const isNull = (value: unknown): value is null => value === null;

export const isUndefined = (value: unknown): value is undefined =>
  typeof value === 'undefined';

export const isNullOrUndefined = <T>(value: T): value is Extract<T, Nullish> =>
  isNull(value) || isUndefined(value);

export const isDefined = <T>(value: T): value is Exclude<T, Nullish> =>
  !isNullOrUndefined(value);

export const isNaNValue = (value: unknown): value is typeof NaN =>
  typeof value === 'number' && Number.isNaN(value);

export const isFalsy = (value: unknown): value is FalsyValue => {
  if (isNullOrUndefined(value)) {
    return true;
  }

  if (value === false) {
    return true;
  }

  if (value === 0 || Object.is(value, -0)) {
    return true;
  }

  if (typeof value === 'bigint' && value === BigInt(0)) {
    return true;
  }

  if (value === '') {
    return true;
  }

  return isNaNValue(value);
};

export const isTruthy = <T>(value: T): value is Exclude<T, FalsyValue> =>
  !isFalsy(value);

export const isTruthyPitfall = (value: unknown): value is TruthyPitfall => {
  if (value === '0' || value === 'false') {
    return true;
  }

  if (Array.isArray(value)) {
    return true;
  }

  return typeof value === 'object' && value !== null;
};
